/**
 * [WTD-1826] Session channel integration, on AWS AppSync Events.
 *
 * The token is never passed in the URL. This application arrives holding a single-use `launch_code`,
 * exchanges it once over HTTPS for the session's access token, and connects with that. The old
 * `getToken` route - where the transport would hand a credential to anyone quoting a session id - is
 * gone.
 */

import {
  ConnectionOptions,
  SessionChannelService,
  SessionChannelResponse,
} from '@curvebeamai/web-connect-service';
import { environment, hasAppSyncConfig } from './config.service';
import {
  clearAuthDataIfForeignSession,
  getUrlLaunchCode,
  stripLaunchCodeFromUrl,
} from './access-validation.service';
import { exchangeLaunchCode, storedAccessToken } from './launch-code.service';
import { SessionConnectionStateEnum } from '../models/SessionConnectionStateEnum';

/**
 * Which kind of failure ended the session flow, for callers that have to choose a page.
 *
 * 'no-session' and 'ended' are genuinely different events wearing the same symptom, and the
 * difference is not recoverable from the error message. A launch code that expired before the
 * viewer finished loading never had a session; a channel that closed after twenty minutes of
 * reading had one and lost it. Telling the first user their session expired is simply untrue, and
 * it sends them looking for a timeout that never happened.
 *
 * 'config' is a third: the environment cannot support a session channel at all. It is not a user
 * event, so neither error page describes it and neither is navigated to - it exists so the message
 * can be rendered with a heading rather than as a bare line, and so whoever deployed the profile
 * sees which key is wrong.
 *
 * Absent (never set) is a fourth outcome and deliberately not a member of this type: an appInit
 * crash is not a session failure at all, so it must not be titled as one. Its message belongs on
 * screen, plainly, rather than behind a navigation that discards it or a heading that misnames it.
 */
export type SessionFailureKind = 'no-session' | 'ended' | 'config';

export type ValidateSessionParams = {
  setSessionState: (state: SessionConnectionStateEnum) => void;
  setInitError: (error: string) => void;
  setSessionFailure: (kind: SessionFailureKind) => void;
};

/**
 * Redeems the launch code if there is one, then opens the session channel.
 *
 * Async because the exchange is a network call. The caller treats this as fire-and-forget and reacts
 * to the state setters, exactly as it did before.
 */
export async function validateSession(params: ValidateSessionParams): Promise<void> {
  const { setSessionState, setInitError, setSessionFailure } = params;

  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get('session_id');

  if (!sessionId) {
    console.error('session_id not provided as URL query parameter');
    // [CurveBeamAI] Deliberately does NOT clear ls.authData. That key is shared by every viewer tab
    // on this origin, and a tab opened without a session_id cannot attribute the stored token to
    // itself — evicting it here would break healthy sibling tabs mid-load (403s on in-flight frames,
    // then a closed session channel). This tab simply refuses to start.
    setInitError('Error: Did not provide session_id URL query parameter');
    setSessionFailure('no-session');
    setSessionState(SessionConnectionStateEnum.DISCONNECTED);
    return;
  }

  // [CurveBeamAI] Drop only a foreign-session token, so this session starts clean. A same-session
  // token is left in place because sibling tabs share this key - and because it is exactly what a
  // reload, or a tab opened without a code of its own, has to fall back on.
  clearAuthDataIfForeignSession(sessionId);

  // Asks about the two ENDPOINTS, where the flow switch asked only whether the object existed. The
  // switch cannot ask this without turning a broken profile into a silent unvalidated boot, so the
  // check belongs here, where there is an error page to put the answer on.
  //
  // It is reachable, unlike the `!appSync` test it replaces: `!!environment.appSync` is truthy for
  // `{}` and for a pair missing one endpoint, so the old guard could not fire on precisely the
  // configurations it was written for, and the flow ran on to build ConnectionOptions with
  // `undefined` hosts.
  const appSync = environment.appSync;
  if (!appSync || !hasAppSyncConfig()) {
    // 'config', which neither redirect acts on: this is a deployment fault, not a session that
    // ended or one that never started, so it stays put and states the technical reason. That is the
    // whole value of this branch, since reaching the library with undefined hosts produces a DNS
    // failure naming no configuration key at all.
    console.error('[SessionChannel] appSync configuration is unusable:', appSync);
    setInitError('Session channel configuration is missing or incomplete (appSync endpoints)');
    setSessionFailure('config');
    setSessionState(SessionConnectionStateEnum.DISCONNECTED);
    return;
  }

  // [WTD-1826] Redeem the code for this session's first access token, rather than letting the
  // transport hand one over.
  const launchCode = getUrlLaunchCode();
  let accessToken: string | null = null;

  if (launchCode) {
    accessToken = await exchangeLaunchCode(launchCode, sessionId);
    // Cleared whether or not it worked - single-use either way, so leaving it in the address bar
    // only risks it being copied or bookmarked. Excluded from the tampering check, so removing it
    // is not itself seen as tampering.
    stripLaunchCodeFromUrl();
  }

  // No code is the normal case for a reload - it was spent on the first load - and for a sibling
  // viewer tab of this session, which was never given one. Both fall back to the shared token.
  if (!accessToken) {
    accessToken = storedAccessToken();
  }

  // With neither, stop. v2 has no getToken route to fall back on: obtaining a credential from the
  // transport was the hole this change closed, so "opened from DMP, or not at all" is the intended
  // end state rather than a limitation.
  if (!accessToken) {
    console.error('[SessionChannel] No launch code supplied and no stored token for this session');
    // 'no-session', and this is the case it exists for. Reaching here means no credential was ever
    // obtained: a launch code that expired before the page finished loading, a second click on a
    // DMP link whose code the first click spent, or a launch URL opened somewhere that has never
    // held this session's token. None of those is an expiry, and the invalid-source page is the one
    // that tells the user the thing that will actually help - open it from DMP.
    setInitError('Please open this viewer from DMP.');
    setSessionFailure('no-session');
    setSessionState(SessionConnectionStateEnum.DISCONNECTED);
    return;
  }

  const connectionOptions = new ConnectionOptions(
    appSync.httpDomain,
    appSync.realtimeDomain,
    sessionId,
    accessToken,
    environment.validateAccessTokenEndpoint || '',
    // The Viewer does NOT own the token store - the library keeps it current from DMP's renewToken
    // broadcasts, which is the whole renewal mechanism for a launched application, and writing it
    // is also what keeps sibling tabs supplied.
    false,
    'viewer'
  );

  const sessionChannel = SessionChannelService.getInstance();

  // SUBSCRIBED BEFORE CONNECTING, which is the opposite of the order that reads naturally.
  //
  // connectionClosedSubject is a plain rxjs Subject with no replay, so anything emitted before
  // this line runs is simply lost - and connect() does emit synchronously. Its promise executor
  // calls AppSyncConnection.connect() inline, which has an early exit for a token carrying no user
  // identity that invokes onClose immediately, and the service's onClose reports the session end
  // through this very subject. Subscribing afterwards would miss it.
  //
  // Not fatal today, because that same onClose also resolves the promise below, so the failure is
  // still reported once. But it is load-bearing by accident: reportSessionEnded sets
  // sessionEndReported, so a first emission that nobody heard also suppresses every later one.
  sessionChannel.connectionClosedSubscription.subscribe((response: SessionChannelResponse) => {
    console.log('Session channel disconnected', response);
    setSessionState(SessionConnectionStateEnum.DISCONNECTED);
    // The one unambiguous 'ended': the channel was open and has closed. DMP's idle timeout arrives
    // here, and so does a sign-out from any other tab of the session.
    setSessionFailure('ended');
    if (response.code === 500) {
      setInitError(
        'There has been an error establishing your session. Please contact CurveBeam AI support team.'
      );
    } else {
      setInitError(response.message);
    }
  });

  // TWO-ARGUMENT then, not .then(...).catch(...). A catch chained after the fulfilment handler sits
  // downstream of it, so a throw from inside the success branch - setSessionState and watchActivity
  // both run there - would be handled by a block whose whole premise is that the channel never
  // opened. Two arguments keep the handler that knows about failure separate from the one that
  // knows about success.
  //
  // The rejection handler is not decoration. SessionChannelService.connect builds its promise with
  // an executor that takes only `resolve`, so the library never rejects deliberately - but the
  // executor calls AppSyncConnection.connect() inline, and that constructs `new WebSocket(...)`,
  // which THROWS for a malformed URL or an invalid subprotocol rather than failing asynchronously.
  // A throw inside an executor rejects the promise. Without this handler that rejection is unhandled
  // and nothing moves sessionState off PENDING, so the viewer sits on 'Pending DMP Access Token
  // validation...' for as long as the tab is open, with no route to either error page.
  // connect() does work BEFORE it returns a promise: setInitialToken writes the auth store, and
  // localStorage.setItem throws outright when storage is unavailable - Safari private mode, a
  // blocked third-party context, or over quota. That throw is not a rejection, so the handler below
  // cannot see it, and without this it escapes validateSession entirely and leaves sessionState on
  // PENDING for as long as the tab is open: the same stuck spinner the rejection handler exists to
  // prevent, reached by the one route it cannot cover.
  let connectPromise: Promise<SessionChannelResponse>;
  try {
    connectPromise = sessionChannel.connect(connectionOptions);
  } catch (error: unknown) {
    console.error('[SessionChannel] connect threw before returning a promise', error);
    setSessionState(SessionConnectionStateEnum.DISCONNECTED);
    setInitError(
      'There has been an error establishing your session. Please contact CurveBeam AI support team.'
    );
    // 'config' for the same reason as the rejection handler below: a credential was obtained, so
    // nothing has expired and a fresh launch would fail identically.
    setSessionFailure('config');
    return;
  }

  connectPromise.then(
    response => {
      if (response.code === 200) {
        console.log('Session channel connected', response);
        setSessionState(SessionConnectionStateEnum.CONNECTED);

        // [WTD-1826] Report this user's activity to DMP so reading a study here keeps the session
        // alive. DMP's idle timer only ever watched its own tab, so a radiologist reading for twenty
        // minutes was logged out mid-read - a bug that predates AppSync and could not be fixed
        // before, because children had no way to publish back.
        sessionChannel.watchActivity();
      } else {
        console.error('Session channel error', response.message);
        setSessionState(SessionConnectionStateEnum.DISCONNECTED);
        setInitError(response.message);
        // A token was obtained and the channel refused it, so a session did exist.
        setSessionFailure('ended');
      }
    },
    (error: unknown) => {
      console.error('[SessionChannel] connect threw rather than resolving', error);
      setSessionState(SessionConnectionStateEnum.DISCONNECTED);
      setInitError(
        'There has been an error establishing your session. Please contact CurveBeam AI support team.'
      );
      // Not 'ended' and not 'no-session'. A credential was obtained, so the session exists as far as
      // this application knows, and nothing about it has expired - the transport failed to start.
      // 'config' keeps the user on the page with the message rather than asserting an expiry or
      // sending them back to DMP for a launch that would fail the same way.
      setSessionFailure('config');
    }
  );
}
